import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORIES } from "../constants/site";
import { ProductCard } from "./ProductCard";
import { ProductModal } from "./ProductModal";
import { SectionHead } from "./Reveal";

export const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("Tous");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => products.filter((p) => (cat === "Tous" || p.category === cat) && p.name.toLowerCase().includes(q.toLowerCase())),
    [products, cat, q]
  );

  return (
    <section id="catalogue" data-testid="catalog-section" className="bg-ice py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHead eyebrow="01 — Catalogue" title="Du matériel choisi pour durer." text="Ordinateurs, imprimantes, accessoires, réseau et consommables : une sélection testée par nos techniciens, garantie et livrée à Dakar." />
          <label className="relative w-full lg:max-w-xs">
            <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="catalog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit…" className="field !pl-8" />
          </label>
        </div>

        <div data-testid="category-tabs" className="mt-12 flex flex-wrap gap-2 border-b border-navy/10 pb-4">
          {["Tous", ...CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`category-tab-${c}`}
              onClick={() => setCat(c)}
              className={`relative rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${cat === c ? "text-white" : "text-navy/70 hover:text-navy"}`}
            >
              {cat === c && <motion.span layoutId="cat-pill" className="absolute inset-0 rounded-full bg-navy" transition={{ type: "spring", stiffness: 350, damping: 30 }} />}
              <span className="relative z-10">{c}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => <div key={i} className="h-96 animate-pulse bg-white" />)}
          </div>
        ) : (
          <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} onView={setSelected} />)}
            </AnimatePresence>
          </motion.div>
        )}
        {!loading && filtered.length === 0 && <p data-testid="catalog-empty" className="mt-10 text-slate-500">Aucun produit ne correspond à votre recherche.</p>}
      </div>
      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </section>
  );
};
